import { DateTime } from "luxon";

import {
  generateClientId,
  getDirectBookingAssignmentFormState,
  getScheduleTypeOrDefaultForPoint,
  getSeededAvailableLoads,
  isBookNowEligibleShipmentType,
} from "utils";

import type { CreateShipmentFormState, ShipmentTemplate } from "types/shipment";

// route points passed to create shipment form when template has no route points
const DEFAULT_ROUTE_POINTS: EditableRoutePointState[] = [
  {
    clientId: "origin",
    stop_type: "ORIGIN",
    stop_operation: "PICKUP" as const,
    point_number: 1,
    start_time: DateTime.now().toFormat("yyyy-MM-dd"),
    isBeingEdited: false,
    inputMode: "autocomplete",
    schedule_type: "DATETIME",
  },
  {
    clientId: "destination",
    stop_type: "DESTINATION",
    stop_operation: "DROPOFF" as const,
    point_number: 2,
    start_time: DateTime.now().toFormat("yyyy-MM-dd"),
    isBeingEdited: false,
    inputMode: "autocomplete",
    schedule_type: "ASAP",
  },
];

function createDisplayableRoutePoint(
  routePoint: ShipmentTemplateRoutePoint,
): EditableRoutePointState {
  const point: EditableRoutePointState = {
    clientId: generateClientId(),
    isBeingEdited: false,
    inputMode: "autocomplete",
    stop_type: routePoint.point.stop_type,
    stop_operation: routePoint.point.stop_operation,
    point_number: routePoint.point.point_number,
    point_location: routePoint.point_location,
    start_time: DateTime.now().toFormat("yyyy-MM-dd"),
    schedule_type: getScheduleTypeOrDefaultForPoint(routePoint.point),
  };
  return point;
}

function addMissingDefaultRoutePoint(
  routePoints: EditableRoutePointState[],
): EditableRoutePointState[] {
  const routePointsCopy = routePoints.slice();
  if (routePointsCopy.length !== 1) {
    throw new Error("Expected route points to have length 1");
  }

  if (routePointsCopy[0].stop_type === "ORIGIN") {
    routePointsCopy.push(DEFAULT_ROUTE_POINTS[1]);
  } else {
    routePointsCopy.unshift(DEFAULT_ROUTE_POINTS[0]);
  }
  return routePointsCopy;
}

function templateIsBookNow(template: ShipmentTemplate): boolean {
  return (
    template.content.shipment?.book_now_currency !== null &&
    template.content.shipment?.book_now_currency !== undefined &&
    template.content.shipment?.book_now_price !== null &&
    template.content.shipment?.book_now_price !== undefined &&
    isBookNowEligibleShipmentType(template.content.shipment.type)
  );
}

interface GetCreateShipmentFormStateFromTemplateContentProps {
  template: ShipmentTemplate;
}

export function getCreateShipmentFormStateFromTemplateContent(
  props: GetCreateShipmentFormStateFromTemplateContentProps,
): CreateShipmentFormState {
  const { template } = props;
  const { content } = template;
  const {
    shipment,
    route,
    project,
    supplier_shipment_info: supplierInfo,
  } = content;

  const routePointsEligibleForDisplay = (route ?? []).filter(
    (point) => point.point_location !== undefined,
  );
  let routePointsToDisplay: EditableRoutePointState[] =
    routePointsEligibleForDisplay.map(createDisplayableRoutePoint);
  if (routePointsToDisplay.length === 1) {
    routePointsToDisplay = addMissingDefaultRoutePoint(routePointsToDisplay);
  }

  const isBookNow = templateIsBookNow(template);
  const shipmentType =
    shipment?.type === "MULTI_SHIPMENT_PROJECT_CHILD"
      ? "SINGLE"
      : (shipment?.type ?? "SINGLE");

  return {
    shipmentType,
    availableLoads: getSeededAvailableLoads(project?.loads_available),
    equipmentTypes: content.equipment_types ?? [],
    service: shipment?.service ?? "",
    commodity: shipment?.commodity ?? "",
    weightLb: shipment?.weight_lb ?? 0,
    footage: shipment?.footage?.toString() ?? "",
    specialRequirements: shipment?.special_requirements ?? "",
    additionalDetails: shipment?.additional_details ?? "",
    preferredPrice: shipment?.preferred_price?.toString() ?? "",
    currency: shipment?.currency ?? null,

    routePoints:
      route == null || route.length === 0
        ? DEFAULT_ROUTE_POINTS
        : routePointsToDisplay,

    // Older/seeded templates may predate this key; fall back to the same
    // default the scratch form uses so the visibility selector never receives
    // undefined (which it cannot recover from).
    notificationSettingsType:
      content.notification_settings_type ?? "ALL_CARRIER_NETWORK",
    // Loaded as confirmed partners regardless of current network membership;
    // UnifiedPartnerSelectorContainer's own mount effect (classifyPrefilledPartnerEmails,
    // ../CreateShipmentForm/components/ConsolidatedShipmentVisibility/components/UnifiedPartnerSelector/utils.tsx)
    // reclassifies any address that no longer matches a confirmed partner into
    // an invite once the live network loads, guarded by isAllPartnersSelected.
    notificationSetingsEmails: content.notification_settings_emails ?? [],
    commodityFreetext: shipment?.commodity_freetext ?? "",
    isBookNow,
    bookNowCurrency: shipment?.book_now_currency ?? "CAD",
    bookNowPrice: isBookNow
      ? (shipment?.book_now_price?.toString(10) ?? "")
      : "",
    customName: shipment?.custom_name ?? "",
    emailNotificationType: "ALL",
    shareeRoleIds: content.sharee_role_ids ?? [],
    shareShipment: (content.sharee_role_ids?.length ?? 0) > 0,
    partnerGroups:
      content.notification_settings_partner_groups?.map((id) =>
        id.toString(),
      ) ?? [],
    customerRoleIds: supplierInfo?.customer_role_ids ?? [],
    externalCustomerIds: supplierInfo?.external_customer_ids ?? [],
    customerInviteEmails: supplierInfo?.customer_invite_emails ?? [],
    customerSnapshots: supplierInfo?.customer_snapshots ?? [],
    files: [],
    quotingUnitOfMeasure: shipment?.quoting_unit_of_measure ?? "ALL_IN",
    analytics: {
      shipmentCreationMode: "TEMPLATE",
      creationStartingTime: DateTime.now(),
      encounteredErrors: [],
      routePointAddressModes:
        shipment?.analytics?.route_point_address_modes ?? {},
    },
    // Templates never store invite emails; any address that turns out not to
    // be a confirmed partner is reclassified into this list by
    // UnifiedPartnerSelectorContainer's mount effect, not here.
    networkInviteEmails: [],
    directBookingAssignment: content.direct_booking
      ? getDirectBookingAssignmentFormState(content.direct_booking)
      : null,
  };
}
